import { useState, useEffect } from "react";
import { BellRing, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { pushSupported, isPushEnabled, enablePush } from "@/lib/push";
import { toast } from "sonner";

const DISMISS_KEY = "shialoc-push-banner-dismissed";

export const PushBanner = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !pushSupported()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (Notification.permission === "denied") return;
    isPushEnabled().then((enabled) => setVisible(!enabled)).catch(() => {});
  }, [user]);

  if (!visible) return null;

  const activate = async () => {
    setLoading(true);
    try {
      await enablePush();
      toast.success(t("saved"));
      setVisible(false);
    } catch (e) {
      if (e.message === "permission_denied") toast.error(t("push_denied"));
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  return (
    <div data-testid="push-banner" className="bg-accent/10 border-b border-accent/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <BellRing className="h-4 w-4 text-accent shrink-0" />
        <p className="text-sm flex-1 min-w-0 truncate">{t("push_banner_text")}</p>
        <Button size="sm" data-testid="push-banner-activate" onClick={activate} disabled={loading} className="rounded-full h-7 text-xs px-4 active:scale-95 shrink-0">
          {loading ? "..." : t("push_banner_btn")}
        </Button>
        <button data-testid="push-banner-dismiss" onClick={dismiss} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
